
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BarChart, PieChart, LineChart } from 'recharts';
import { ResponsiveContainer, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Pie, Cell, Line } from 'recharts';
import { Flag, Users, Calendar, DollarSign } from 'lucide-react';

// Mock data for demo purposes
const referralData = [
  { month: 'Jan', referrals: 65, completions: 40 },
  { month: 'Feb', referrals: 80, completions: 55 },
  { month: 'Mar', referrals: 120, completions: 70 },
  { month: 'Apr', referrals: 90, completions: 60 }
];

const conversionData = [
  { name: 'Converted', value: 68, color: '#16a34a' },
  { name: 'Pending', value: 32, color: '#f59e0b' }
];

const channelData = [
  { name: 'Facebook', value: 35, color: '#1877f2' },
  { name: 'Twitter', value: 15, color: '#1da1f2' },
  { name: 'WhatsApp', value: 25, color: '#25d366' },
  { name: 'Email', value: 20, color: '#6b7280' },
  { name: 'Instagram', value: 5, color: '#c13584' }
];

const campaignStats = [
  { name: 'Active Campaigns', value: 4, icon: Flag, color: 'text-green-500' },
  { name: 'Total Referrals', value: 355, icon: Users, color: 'text-blue-500' },
  { name: 'Avg. Campaign Duration', value: '48 days', icon: Calendar, color: 'text-amber-500' },
  { name: 'Total Rewards Issued', value: '$6,420', icon: DollarSign, color: 'text-purple-500' }
];

const CampaignAnalyticsSection: React.FC = () => {
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold">Campaign Analytics</h1>
        <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select Campaign" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            <SelectItem value="holiday">Holiday Double Referral</SelectItem>
            <SelectItem value="early">Early Bird Referral</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {campaignStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4 flex items-center">
                <div className={`p-3 rounded-full mr-4 ${stat.color} bg-gray-100`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{stat.name}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="channels">Share Channels</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Monthly Referral Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={referralData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="referrals" name="Total Referrals" fill="#8884d8" />
                    <Bar dataKey="completions" name="Completed" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="performance">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Conversion Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center">
                <div className="w-64">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={conversionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {conversionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="channels">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Referral Share Channels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center">
                <div className="w-64">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={channelData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {channelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignAnalyticsSection;
